/**
 * TimeTravel context — exposes a `viewAsOf` rewind value that the CEO
 * dashboard uses to scale historical KPIs/charts down to "as of N days
 * ago". Purely demo / mock — derives a multiplier from the offset and
 * lets the CEO panels apply it to live numbers.
 *
 *   const { viewAsOf, setViewAsOf, multiplier, label } = useTimeTravel();
 *
 * `viewAsOf === 0` → today (no rewind).
 * `viewAsOf === 30` → "30 days ago" (multiplier ~0.92, depending on growth).
 */
import React, { createContext, useContext, useMemo, useState } from "react";

const TimeTravelCtx = createContext({
  viewAsOf: 0,
  setViewAsOf: () => {},
  multiplier: 1,
  label: "Today",
  enabled: false,
});

const STOPS = [
  { value: 0,    label: "Today" },
  { value: 30,   label: "30 days ago" },
  { value: 60,   label: "60 days ago" },
  { value: 90,   label: "90 days ago" },
  { value: 180,  label: "6 months ago" },
  { value: 365,  label: "1 year ago" },
];

// Smooth growth curve so further rewinds = smaller numbers.
// Today = 1.00 · 30d = 0.92 · 60d = 0.85 · 90d = 0.78 · 180d = 0.62 · 365d = 0.42
function growthMultiplier(days) {
  if (!days) return 1;
  // Compound monthly growth of ~3% (slightly above realistic adoption pace
  // — this makes the demo journey feel like meaningful progress).
  const months = days / 30;
  return Math.max(0.4, Math.pow(1 / 1.03, months));
}

export function TimeTravelProvider({ children }) {
  const [viewAsOf, setViewAsOf] = useState(0);
  const value = useMemo(() => {
    const stop = STOPS.find((s) => s.value === viewAsOf) || STOPS[0];
    return {
      viewAsOf,
      setViewAsOf,
      multiplier: growthMultiplier(viewAsOf),
      label: stop.label,
      enabled: true,
      stops: STOPS,
    };
  }, [viewAsOf]);

  return <TimeTravelCtx.Provider value={value}>{children}</TimeTravelCtx.Provider>;
}

export function useTimeTravel() {
  return useContext(TimeTravelCtx);
}

export const TIME_STOPS = STOPS;
